import {
  fromEnumAsString,
  toEnumFromString,
} from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import {
  DataModel,
  DataModelBuilder,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { PassPrintFormat } from './pass-print-format.js';
import { Language } from './language.js';
import { TicketPrintFormat } from './ticket-print-format.js';

export class PrintingSettings extends DataModel {
  static readonly TYPE_ID: string = 'c06da7fd00eb4dd6b47e0a884df3a940';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly automaticTicketPrinting: boolean,
    public readonly automaticPassPrinting: boolean,
    public readonly ticketFormat: TicketPrintFormat,
    public readonly passFormat: PassPrintFormat,
    public readonly ticketLanguage: Language,
    public readonly passLanguage: Language,
  ) {
    super();
  }

  get $typeId(): string {
    return PrintingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PrintingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    automaticTicketPrinting: boolean,
    automaticPassPrinting: boolean,
    ticketFormat: TicketPrintFormat,
    passFormat: PassPrintFormat,
    ticketLanguage: Language,
    passLanguage: Language,
  }): PrintingSettings {
    return new PrintingSettings(
        args.automaticTicketPrinting,
        args.automaticPassPrinting,
        args.ticketFormat,
        args.passFormat,
        args.ticketLanguage,
        args.passLanguage,
    );
  }

  static empty(): PrintingSettings {
    return PrintingSettings.create({
      automaticTicketPrinting: false,
      automaticPassPrinting: false,
      ticketFormat: TicketPrintFormat.A4,
      passFormat: PassPrintFormat.PassSticker,
      ticketLanguage: Language.Hungarian,
      passLanguage: Language.Hungarian,
    });
  }

  static fromMap(_context: SerializationContext, map: SerializedData): PrintingSettings {
    return PrintingSettings.create({
      automaticTicketPrinting: MapUtils.getBoolean(map, 'ticketAutomaticPrinting'),
      automaticPassPrinting: MapUtils.getBoolean(map, 'passAutomaticPrinting'),
      ticketFormat: toEnumFromString(MapUtils.getString(map, 'ticketFormat'), TicketPrintFormat),
      passFormat: toEnumFromString(MapUtils.getString(map, 'passFormat'), PassPrintFormat),
      ticketLanguage: toEnumFromString(MapUtils.getString(map, 'ticketLanguage'), Language),
      passLanguage: toEnumFromString(MapUtils.getString(map, 'passLanguage'), Language),
    });
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'ticketAutomaticPrinting': this.automaticTicketPrinting,
      'passAutomaticPrinting': this.automaticPassPrinting,
      'ticketFormat': fromEnumAsString(this.ticketFormat),
      'passFormat': fromEnumAsString(this.passFormat),
      'ticketLanguage': fromEnumAsString(this.ticketLanguage),
      'passLanguage': fromEnumAsString(this.passLanguage),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    automaticTicketPrinting = this.automaticTicketPrinting,
    automaticPassPrinting = this.automaticPassPrinting,
    ticketFormat = this.ticketFormat,
    passFormat = this.passFormat,
    ticketLanguage = this.ticketLanguage,
    passLanguage = this.passLanguage,
  }: {
    automaticTicketPrinting?: boolean,
    automaticPassPrinting?: boolean,
    ticketFormat?: TicketPrintFormat,
    passFormat?: PassPrintFormat,
    ticketLanguage?: Language,
    passLanguage?: Language,
  } = {}): this {
    return PrintingSettings.create({
      automaticTicketPrinting,
      automaticPassPrinting,
      ticketFormat,
      passFormat,
      ticketLanguage,
      passLanguage,
    }) as this;
  }

  isSame(other: PrintingSettings): boolean {
    if(other === this) {
      return true;
    }

    return other.automaticTicketPrinting === this.automaticTicketPrinting &&
      other.automaticPassPrinting === this.automaticPassPrinting &&
      other.ticketFormat === this.ticketFormat &&
      other.passFormat === this.passFormat &&
      other.ticketLanguage === this.ticketLanguage &&
      other.passLanguage === this.passLanguage;
  }
}

export class PrintingSettingsBuilder extends DataModelBuilder<PrintingSettings> {
  build(context: SerializationContext, map: SerializedData): PrintingSettings {
    return PrintingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(_context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PrintingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PrintingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `ticketLanguage` and `passLanguage` of type {@link Language}
     */
    if(version < 2) {
      map['ticketLanguage'] = fromEnumAsString(Language.Hungarian);
      map['passLanguage'] = fromEnumAsString(Language.Hungarian);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
